<template>
  <div id="terms_condition">
    <form
      id="tnc-form"
      data-abide
      novalidate
    >
      <attachments
        :files="files"
        :noHighlight="true"
        :zipName="zipName"
      />

      <div v-show="isContractorView">
        <div class="clearfix">
          <div
            v-if="tncChecked.length < 1"
            class="errorTab"
            style="min-height:30px;"
          >
            <input
              id="no-tnc-exception-input"
              type="checkbox"
              :value="false"
              required
              @click.prevent="event => false"
            >
          </div>
          <div
            v-for="(item, index) in tncChecked"
            :key="`tnc-key-${index}`"
            class="tnc-item"
            :class="{'tnc-item-last': index===lastIndex, 'has-checkbox': editView}"
          >
            <template v-if="isTender || !tncItemList[index].itqExclude">
              <input
                v-if="editView"
                :id="`tnc-agree${index}`"
                type="checkbox"
                :value="item.checked"
                required
                @change="event => updateTncCheckedValue(index, event.target.checked)"
              >
              <label
                :for="editView ? `tnc-agree${index}` : ''"
                class="tender-font"
                :class="{ errorTab: (!item.checked)&&checkForm }"
                v-html="tncItemList[index].content"
              />
            </template>
          </div>
          <div
            v-if="isOldTerm"
            class="clearfix tnc-total-fee"
            :class="{'has-checkbox': editView}"
          >
            <span
              class="tender-currency"
              :class="{ errorTab: (tncChecked.length > 0 && !tncChecked[lastIndex].checked)&&checkForm }"
            >
              {{ currencyCode }}
            </span>
            <span
              class="tender-total-fee"
              :class="{ errorTab: (tncChecked.length > 0 && !tncChecked[lastIndex].checked)&&checkForm }"
            >
              {{ currencyFormatter.format(totalFee, 'USD') }}
            </span>
          </div>
          <div
            class="general-items"
            :class="{'has-checkbox': editView}"
          >
            <label
              v-if="projectStart"
              v-html="$t(`tender.general.projectStart`)"
            />
            <template v-if="projectStart">
              <p v-if="isOldTerm">
                <span v-html="$t(`tender.general.oldProjectStartDesc`, {projectStart})" />
              </p>
              <p v-else>
                <span v-html="$t(`tender.general.projectStartDesc`, {projectStart})" />
              </p>
            </template>
            <label
              v-if="shouldShowAdditionalItems"
              v-html="$t(`tender.general.optionalItems`)"
            />
            <ul class="additionalItems-list">
              <template v-for="option in optionalItems">
                <li
                  v-if="option.selected"
                  :key="option.name"
                  class="row"
                >
                  <div class="additionalItems-label">
                    {{ option.name }}
                  </div>
                  <div class="additionalItems-text">
                    {{ option.value }} {{ option.text }}
                  </div>
                </li>
              </template>
            </ul>
          </div>

          <div
            class="row tnc-name-position"
            :class="{'has-checkbox': editView}"
          >
            <div class="column">
              <label
                for="tnc-agree-name"
                class="tender-font"
                :class="{'label-min-width': editView}"
                v-html="$t(`tender.general.Name`)"
              />
              <input
                v-if="editView"
                id="tnc-agree-name"
                v-model="tncInfo.name"
                type="text"
                class="tnc-input"
                required
                @change="updateTnc()"
              >
              <label
                v-if="!editView"
                for="tnc-agree-name"
                class="tender-font"
              >
                {{ tncInfo.name }}
              </label>
            </div>
            <div class="column">
              <label
                for="tnc-agree-position"
                class="tender-font"
                :class="{'label-min-width': editView}"
                v-html="$t(`tender.general.Position`)"
              />
              <input
                v-if="editView"
                id="tnc-agree-position"
                v-model="tncInfo.position"
                type="text"
                class="tnc-input"
                required
                @change="updateTnc()"
              >
              <label
                v-if="!editView"
                for="tnc-agree-position"
                class="tender-font"
              >
                {{ tncInfo.position }}
              </label>
            </div>
          </div>
          <div
            v-show="tncInfo.companyName"
            class="row declaration-on-behalf"
            :class="{'has-checkbox': editView}"
          >
            <div class="column">
              <label class="tender-font"><span v-html="$t(`tender.general.onBehalfOf`)" /> {{ tncInfo.companyName }}</label>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import lodash from 'lodash'

import Attachments from '@/modules/tenders/view/Attachments'
import mutationTypes from '@/store/mutation-types'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import CurrencyFormatter from 'currency-formatter'

import { getTermsConditions } from '@/api/docs/request'
import { getDepartmentByLocationCode } from '@/api/locations/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { mapGetters, mapMutations, mapState } from 'vuex'

function getDefaultData () {
  return {
    files: [],
    checkForm: false,
    specialIndex1: 1,
    specialIndex2: 6,
    currencyFormatter: CurrencyFormatter,
  }
}
export default {
  name: 'TermsConditions',
  components: {
    Attachments,
  },

  props: {
    isTender: {
      type: Boolean,
      required: true,
    },
    tenderId: {
      type: String,
    },
    tenderStatus: {
      type: String,
    },
    isContractorView: {
      type: Boolean,
      default: false,
    },
    tncInfo: {
      type: Object,
      default () {
        return {
          name: null,
          position: null,
          companyName: null,
        }
      },
    },
    totalFee: {
      default: '--',
    },
    editView: {
      type: Boolean,
      default: true,
    },
    zipName: {
      type: String,
      default: '',
    },
  },

  data: () => { return { ...getDefaultData(), sequencePromise: Promise.resolve() } },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'isAddendum', 'isOldTerm', 'currentTermVersion', 'tncI18nPath', 'tncI18nTermsPath']),
    ...mapState('tenders', {
      validityPeriod (state) {
        return deep(state.currentTender, 'general.validityPeriod')
      },
      projectStart (state) {
        return deep(state.currentTender, 'general.projectStart')
      },
      optionalItems (state) {
        return deep(state.currentTender, 'general.optionalItems')
      },
      tncChecked (state) {
        return state.tncChecked
      },
      shouldShowAdditionalItems () {
        if (!this.optionalItems) return false
        const checkedOptions = this.optionalItems
          .filter(item => item.selected)
        return checkedOptions.length > 0
      },
      validityPeriodHtml () {
        return `<span style="font-style: italic">${this.validityPeriod} ${this.$t('days')}</span>`
      },
      totalFeeHtml () {
        return `<span class="tender-currency">${this.currencyCode}</span>
                <span class="tender-total-fee">${this.currencyFormatter.format(this.totalFee, 'USD')}</span>`
      },
      tncItemList () {
        return this.tncChecked.map((item, index) => {
          return this.$te(this.tncI18nTermsPath)
            ? {
              content: this.$t(
                `${this.tncI18nTermsPath}[${index}].value`,
                {validityPeriod: this.validityPeriodHtml, totalFee: this.totalFeeHtml},
              ),
              itqExclude: this.$t(`${this.tncI18nTermsPath}`)[index].itqExclude,
            }
            : {}
        })
      },
    }),
    general () { return this.currentTender.general || {} },
    lastIndex () {
      return this.tncItemList.length - 1
    },
    currencyCode () {
      return lodash.get(this.general, 'purchaseRequisition.currencyCode')
    },
  },
  watch: {
    isTender () {
      this.sendLoadFilesRequestsequentially()
    },
    tenderStatus () {
      this.sendLoadFilesRequestsequentially()
    },
    tenderId () {
      this.sendLoadFilesRequestsequentially()
    },
    general () {
      this.sendLoadFilesRequestsequentially()
    },
  },
  async mounted () {
    this.sendLoadFilesRequestsequentially()
    EventBus.$on('prchanged', () => {
      this.sendLoadFilesRequestsequentially()
    })
  },
  destroyed () {
    this.resetTncChecked()
    EventBus.$off('prchanged')
  },
  methods: {
    ...mapMutations('tenders', {
      updateTncChecked: mutationTypes.UPDATE_TNC,
      resetTncChecked: mutationTypes.RESET_TNC,
      updateDepartment: mutationTypes.TENDER_UPDATE_DEPARTMENT,
    }),
    reset (keep) {
      const def = getDefaultData()
      def[keep] = this[keep]
      Object.assign(this.$data, def)
    },
    updateTncCheckedValue (index, checked) {
      this.updateTncChecked({index, checked})
      this.updateTnc()
    },
    updateTnc () {
      formUtils.setDirty()
      this.$emit('update:tncInfo', this.tncInfo)
    },
    async loadFiles () {
      const siteOfCurTender = lodash.get(this.currentTender, 'general.site')
      if (!(this.tenderId || this.general.id)) {
        return
      }
      if (siteOfCurTender) {
        const dept = await getDepartmentByLocationCode(siteOfCurTender)
        if (!dept) {
          notification.warn(i18n.t('settings.site.noDeptSite'))
          return
        }
        this.updateDepartment({department: dept})

        this.files = await getTermsConditions({
          isContractorView: this.isContractorView,
          tenderId: this.tenderId || this.general.id,
          status: this.tenderStatus,
          isTender: this.isTender,
          isAddendum: this.isAddendum,
        })
      } else {
        this.files = []
      }
    },
    validate () {
      this.form = $('#tnc-form')
      this.form.on('submit', (ev) => {
        ev.preventDefault()
      })
      this.abide = new Foundation.Abide(this.form, {
        liveValidate: false,
      })

      this.checkForm = true
      this.abide.validateForm()
      return $('#tnc-form input.is-invalid-input').length !== 0 ||
        $('#tnc-form textarea.is-invalid-input').length !== 0
    },
    clearErrorClasses () {
      if ($('#tnc-form .is-invalid-input').length > 0 && this.abide) {
        this.abide.removeErrorClasses($('#tnc-form .is-invalid-input'))
      }
      this.checkForm = false
    },
    sendLoadFilesRequestsequentially () {
      this.sequencePromise = this.sequencePromise.then(() => this.loadFiles())
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/settings';
$grey-color: #e0e0e0;

.additionalItems-list {
  list-style-type: none;
  margin-left: 0;
}

.tnc-name-position {
  margin-top: 10px;
  div>label {
    font-weight: bold;
    margin: 0.5rem 0.5rem 0.5rem 0;
    display: inline-block;
  }
}

.label-min-width {
    min-width: 5rem;
}

div>input {
  display: inline-block;
}

.tnc-item-last {
  margin-bottom:0!important;
}

.tnc-item {
  margin-bottom: 20px;
  label {
    display: block;
    margin-left: 0;
    line-height: 1.8;
  }
  input[type=checkbox] {
    float: left;
    margin-left: -20px;
    margin-top: 6px;
  }
  .is-invalid-label {
    background-color: #f9ecea;
  }
}

.has-checkbox {
  margin-left: 20px;
}

.tnc-total-fee {
  margin-top: 10px;
  text-align: left;
}

.general-items {
  margin-top: 10px;
  label {
    font-size: 14px;
    line-height: 1.43;
    color: #0C3A58;
    margin-bottom: 5px;
  }
  p, ul li {
    font-weight: bold;
    color: #0C3A58;
  }
}

.tnc-input {
  background-color: white;
  border: solid 1px #5f7988;
  width: 60%;
  min-width: 160px;
  margin-right: 10px;
}

.tender-font ::v-deep .tender-total-fee {
  font-size: 16px;
  font-family: Verdana;
  color: #7E9943;
}

.tender-font ::v-deep .tender-currency {
  font-family: Verdana;
  font-weight: bold;
  color: #0c3a58;
}

input.is-invalid-input {
  background-color: #f9ecea!important;
}

input[type="checkbox"].is-invalid-input{
  background: red;
}

.errorTab {
  background-color: #f9ecea!important;
}

.declaration-on-behalf {
  label {
    font-weight: bold;
    margin-top: 10px;
  }
}
.general-items {
  .additionalItems-label {
    width: 240px;
  }
}
#no-tnc-exception-input{
  border: none;
  background: none;
}
@media print {
  .tnc-name-position .column {
    flex: none;
    width: 100%;
    max-width: 100%;

    .tnc-input {
      border: none;
      margin-bottom: 0;
    }
  }
}
</style>
